// src/api/filesApi.ts

import { baseApi } from './baseApi';
import type {
  FileAsset,
  ReceivedFile,
  CreateFileRequest,
  UpdateFileRequest,
  ShareFileRequest,
  FileShare,
} from '../types/api';

interface GetFilesParams {
  workspaceId?: string;
  q?: string;
  contactId?: string;
  projectId?: string;
  taskId?: string;
}

interface GetReceivedFilesParams {
  workspaceId?: string;
}

export const filesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFile: builder.query<FileAsset, string>({
      query: (id) => `/files/${id}`,
      providesTags: (result, error, id) => [{ type: 'File', id }],
    }),

    getFiles: builder.query<FileAsset[], GetFilesParams>({
      query: (params) => ({
        url: '/files',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.q && { q: params.q }),
          ...(params.contactId && { contactId: params.contactId }),
          ...(params.projectId && { projectId: params.projectId }),
          ...(params.taskId && { taskId: params.taskId }),
        },
      }),
      providesTags: (result, error, { workspaceId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'File' as const, id })),
              { type: 'File', id: workspaceId || 'LIST' },
            ]
          : [{ type: 'File', id: workspaceId || 'LIST' }],
    }),

    getReceivedFiles: builder.query<ReceivedFile[], GetReceivedFilesParams>({
      query: (params) => ({
        url: '/files/received',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
        },
      }),
      providesTags: (result, error, { workspaceId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'File' as const, id: `received-${id}` })),
              { type: 'File', id: `received-${workspaceId || 'LIST'}` },
            ]
          : [{ type: 'File', id: `received-${workspaceId || 'LIST'}` }],
    }),

    createFile: builder.mutation<FileAsset, CreateFileRequest>({
      query: (body) => ({
        url: '/files',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId }) => [
        { type: 'File', id: workspaceId || 'LIST' },
      ],
    }),

    updateFile: builder.mutation<FileAsset, { id: string } & UpdateFileRequest>({
      query: ({ id, ...body }) => ({
        url: `/files/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'File', id },
        { type: 'File', id: 'LIST' },
      ],
    }),

    deleteFile: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/files/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'File', id },
        { type: 'File', id: 'LIST' },
      ],
    }),

    shareFile: builder.mutation<FileShare, { id: string } & ShareFileRequest>({
      query: ({ id, ...body }) => ({
        url: `/files/${id}/share`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'File', id },
        { type: 'File', id: 'received-LIST' },
      ],
    }),
  }),
});

export const {
  useGetFileQuery,
  useGetFilesQuery,
  useGetReceivedFilesQuery,
  useCreateFileMutation,
  useUpdateFileMutation,
  useDeleteFileMutation,
  useShareFileMutation,
} = filesApi;
